import { AbstractDate } from "@linkfluence/models/date";
import { Timezone } from "@linkfluence/models/timezone";
import { Clause } from "@radarly/models/clause";
import {
  Dataviz,
  DatavizName,
  getDefaultDataviz,
  isAIDataviz,
} from "@radarly/models/dataviz";
import { Card } from "..";
import { AIFragment } from "@radarly/models/search";
import { AICard } from "../model";
import { CardFactory, CardFragmentsFactory } from "./base";
import { ICardFactory } from "./model";

interface AICardFactoryOptions {
  type?: AIFragment["analysis"]["type"];
  includeNegativeAnalysis?: AIFragment["analysis"]["includeNegativeAnalysis"];
  useInsightPageAIContext?: AIFragment["analysis"]["useInsightPageAIContext"];
  clause?: Clause;
  from?: AbstractDate;
  to?: AbstractDate;
  tz?: Timezone;
}

const getDefaultAIValuesFromInput = (input?: AICardFactoryOptions) => ({
  type: input?.type ?? "general-analysis",
  includeNegativeAnalysis: input?.includeNegativeAnalysis ?? false,
  useInsightPageAIContext: input?.useInsightPageAIContext ?? true,
});

export class AIFragmentsFactory extends CardFragmentsFactory {
  static create(input?: AICardFactoryOptions) {
    const factory = new AIFragmentsFactory();
    return factory.getFragments(input);
  }
  static createFromCard(card: Card) {
    const factory = new AIFragmentsFactory();
    const { from, to } = factory.getPeriod(card);
    return factory.getFragments({
      clause: factory.getClauseFromCard(card),
      from,
      to,
    });
  }
  private getFragments(input?: AICardFactoryOptions): [AIFragment] {
    return [this.getFragment(input)];
  }
  private getFragment(input?: AICardFactoryOptions): AIFragment {
    return {
      request: {
        query: this.getQuery(input),
      },
      type: "ai-analysis",
      analysis: {
        ...getDefaultAIValuesFromInput(input),
      },
    };
  }
}
export class AICardFactory
  implements ICardFactory<AICard, AICardFactoryOptions>
{
  public static getInstance() {
    return new AICardFactory();
  }
  public fromCard(
    workspaceId: number,
    projectFocuses: number[],
    card: Card,
    input?: AICardFactoryOptions & {
      dataviz?: AICard["dataviz"];
    }
  ) {
    const fragments = AIFragmentsFactory.createFromCard(card);
    const dataviz = this.createDataviz(input?.dataviz || card.dataviz, input);
    return CardFactory.getInstance<AICard>().createCard(
      workspaceId,
      projectFocuses,
      "ai-analysis",
      card.title,
      fragments,
      dataviz
    );
  }
  public fromInput(
    workspaceId: number,
    projectFocuses: number[],
    title: string,
    input?: AICardFactoryOptions & {
      dataviz?: Dataviz["name"];
      fullDataviz?: Dataviz;
    }
  ) {
    const fragments = AIFragmentsFactory.create(input);
    const dataviz = this.createDataviz(
      input?.fullDataviz ?? getDefaultDataviz(DatavizName.AI),
      input
    );
    return CardFactory.getInstance<AICard>().createCard(
      workspaceId,
      projectFocuses,
      "ai-analysis",
      title,
      fragments,
      dataviz
    );
  }

  public createDataviz(dataviz: Dataviz, input: AICardFactoryOptions) {
    if (isAIDataviz(dataviz)) {
      return dataviz;
    } else {
      return {
        ...(getDefaultDataviz(DatavizName.AI) as AICard["dataviz"]),
      };
    }
  }
}
